import lambdaApi from 'api/LambdaApi';
import { AxiosError } from 'axios';
import {
    CustomerWithReference,
    NewReferenceModel,
    ReferenceModel,
} from 'generated-types/skills/customer_references';
import { useMutation, useQuery } from 'react-query';

type MutationOptions = {
    onSuccess?: () => void;
    onError?: (error: AxiosError) => void;
};

interface ReferenceModelWithFile extends ReferenceModel {
    image_file?: File;
    logo_file?: File;
}

interface NewReferenceModelWithFile extends NewReferenceModel {
    image_file?: File;
    logo_file?: File;
}

interface UseCustomerWithoutReferencesReturn {
    customersWithoutReference: CustomerWithReference[] | undefined;
    customersWithoutReferenceIsLoading: boolean;
    customersWithoutReferenceRefetch: () => void;
    customersWithoutReferenceIsError: boolean;
}

interface UseCustomerReferencesReturn extends UseCustomerWithoutReferencesReturn {
    customerReferences: CustomerWithReference[];
    updateReference: {
        mutate: (
            updatedData: ReferenceModel,
            options?: {
                onSuccess?: () => void;
                onError?: (error: AxiosError) => void;
            },
        ) => void;
        isLoading: boolean;
    };
    deleteReference: {
        mutate: (
            uuid: string,
            options?: {
                onSuccess?: () => void;
                onError?: (error: AxiosError) => void;
            },
        ) => void;
        isLoading: boolean;
    };
    createReference: {
        mutate: (
            newReferenceData: NewReferenceModel,
            customer_id: number,
            options?: {
                onSuccess?: () => void;
                onError?: (error: AxiosError) => void;
            },
        ) => void;
        isLoading: boolean;
    };
    isLoading: boolean;
    refetch: () => void;
    isError: boolean;
}

const referencesUri = '/skills/customer-references';
const customersWithoutReferencesUri = '/skills/customers-without-reference';
const imageUri = `${referencesUri}/images`;

export const uploadReferenceImage = async (filename: string, file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image_name', filename);
    formData.append('image_file', file);

    const uploadRes = await lambdaApi.post(imageUri, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });

    return uploadRes.data.image_url;
};

const useCustomerReferencesHook = (): UseCustomerReferencesReturn => {
    const { data, isLoading, refetch, isError } = useQuery<CustomerWithReference[], AxiosError>(
        ['customer_references'],
        () => lambdaApi.get<CustomerWithReference[]>(referencesUri).then(res => res.data),
        {
            retry: false,
            enabled: true,
        },
    );

    const {
        data: customersWithoutReferenceData,
        isLoading: customersWithoutReferenceIsLoading,
        refetch: customersWithoutReferenceRefetch,
        isError: customersWithoutReferenceIsError,
    } = useQuery<CustomerWithReference[], AxiosError>(
        ['customers_without_reference'],
        () =>
            lambdaApi
                .get<CustomerWithReference[]>(customersWithoutReferencesUri)
                .then(res => res.data),
        {
            retry: false,
            enabled: true,
        },
    );

    const updateReferenceMutation = useMutation<unknown, AxiosError, ReferenceModelWithFile>(
        async (updatedData: ReferenceModelWithFile) => {
            if (updatedData.image_file) {
                updatedData.image_url = await uploadReferenceImage(
                    updatedData.reference_uuid,
                    updatedData.image_file,
                );
            }
            if (updatedData.logo_file) {
                updatedData.logo_url = await uploadReferenceImage(
                    updatedData.reference_uuid + '-logo',
                    updatedData.logo_file,
                );
            }
            const updateUri = `${referencesUri}/${updatedData.reference_uuid}`;
            const res = await lambdaApi.put(updateUri, updatedData);
            return res.data;
        },
    );

    const deleteReferenceMutation = useMutation<void, AxiosError, string>(
        async (reference_uuid: string) => {
            const deleteUri = `${referencesUri}/${reference_uuid}`;
            const res = await lambdaApi.delete(deleteUri);
            const deleteImagesUri = `${imageUri}/${reference_uuid}`;
            await lambdaApi.delete(deleteImagesUri);
            return res.data;
        },
    );

    const createReferenceMutation = useMutation<
        unknown,
        AxiosError,
        { customer_id: string; data: NewReferenceModelWithFile }
    >(async ({ customer_id, data }) => {
        const dataWithCustomerId = { ...data, customer_id };

        const uriWithCustomerId = `${referencesUri}`;
        const res = await lambdaApi.post(uriWithCustomerId, dataWithCustomerId);
        // Upload images to s3 after getting reference_uuid
        if (data.image_file || data.logo_file) {
            const updatedData = res.data as ReferenceModel;
            if (data.image_file) {
                updatedData.image_url = await uploadReferenceImage(
                    res.data.reference_uuid,
                    data.image_file,
                );
            }
            if (data.logo_file) {
                updatedData.logo_url = await uploadReferenceImage(
                    res.data.reference_uuid + '-logo',
                    data.logo_file,
                );
            }
            // update urls to neo4j
            const updateUri = `${referencesUri}/${updatedData.reference_uuid}`;
            const updateRes = await lambdaApi.put(updateUri, updatedData);
            return updateRes.data;
        }

        return res.data;
    });

    const references = (data as CustomerWithReference[]) || [];

    return {
        customersWithoutReferenceIsLoading,
        customersWithoutReferenceRefetch,
        customersWithoutReferenceIsError,
        customersWithoutReference:
            customersWithoutReferenceData?.sort((a, b) =>
                a.customer.name.localeCompare(b.customer.name),
            ) ?? [],
        customerReferences:
            references.sort((a, b) => a.customer.name.localeCompare(b.customer.name)) ?? [],
        updateReference: {
            mutate: (
                updatedData: ReferenceModel,
                options?: {
                    onSuccess?: () => void;
                    onError?: (error: AxiosError) => void;
                },
            ) => {
                updateReferenceMutation.mutate(updatedData, options);
            },
            isLoading: updateReferenceMutation.isLoading,
        },
        deleteReference: {
            mutate: (
                uuid: string,
                options?: {
                    onSuccess?: () => void;
                    onError?: (error: AxiosError) => void;
                },
            ) => {
                deleteReferenceMutation.mutate(uuid, options);
            },
            isLoading: deleteReferenceMutation.isLoading,
        },
        createReference: {
            mutate: (
                newReferenceData: NewReferenceModel,
                customer_id: number,
                options?: MutationOptions,
            ) =>
                createReferenceMutation.mutate(
                    {
                        data: newReferenceData,
                        customer_id: customer_id.toString(),
                    },
                    options,
                ),
            isLoading: createReferenceMutation.isLoading,
        },
        isLoading,
        refetch,
        isError,
    };
};

export { useCustomerReferencesHook };
